import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bug,
  ChevronRight,
  Download,
  ExternalLink,
  GalleryVertical,
  LayoutGrid,
  Phone,
  Search,
  Sparkles,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChrome, faFirefox } from "@fortawesome/free-brands-svg-icons";
import ActionButton from "./ActionButton";
import { formatProjectTitle } from "../../utils/formatProjectTitle";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const LE_GROUP_ID = "__le_projects__";

const isValidLink = (link) => {
  if (!link) return false;
  const trimmed = link.trim().toLowerCase();
  return trimmed && trimmed !== "na";
};

const isValidDialNumber = (dialNumber) => {
  if (!dialNumber) return false;
  const s = String(dialNumber).trim();
  return s.length > 0 && s.toLowerCase() !== "na";
};

const divisionColor = (division) => {
  const normalized = Array.isArray(division) ? division.find(Boolean) : division;
  const key = String(normalized || "").toLowerCase();
  if (key.includes("health")) return { bg: "bg-emerald-500", text: "text-emerald-500" };
  if (key.includes("system")) return { bg: "bg-sky-500", text: "text-sky-500" };
  if (key.includes("locums")) return { bg: "bg-amber-500", text: "text-amber-500" };
  return { bg: "bg-indigo-500", text: "text-indigo-500" };
};

function ActionIcon({
  href,
  title,
  children,
  className,
  disabled = false,
  onClick,
  download = false,
}) {
  const base =
    "relative group/action inline-flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200";
  if (!href) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cx(base, className, disabled && "opacity-50 pointer-events-none")}
      >
        {children}
        {title && (
          <div className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg">
            {title}
          </div>
        )}
      </button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      download={download || undefined}
      onClick={onClick}
      className={cx(base, className)}
    >
      {children}
      {title && (
        <div className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg">
          {title}
        </div>
      )}
    </a>
  );
}

function AppGlyph({ title, division }) {
  const initials = useMemo(() => {
    const t = formatProjectTitle(title || "");
    const parts = t.split(/\s+/).filter(Boolean);
    const two = parts.slice(0, 2).map((p) => p[0]).join("");
    return (two || "?").toUpperCase();
  }, [title]);

  const c = divisionColor(division);

  return (
    <div
      className={cx(
        "w-9 h-9 rounded-lg flex items-center justify-center",
        "bg-opacity-10",
        c.bg,
        c.text
      )}
      aria-hidden="true"
    >
      <span className="text-[11px] font-black tracking-widest">{initials}</span>
    </div>
  );
}

function ProjectLogo({ project, size = "sm" }) {
  const src = project?.images?.[0] || "";
  const title = formatProjectTitle(project?.title || "");

  const dims =
    size === "lg"
      ? { wrap: "w-20 h-20 rounded-2xl", img: "w-16 h-16" }
      : size === "md"
        ? { wrap: "w-9 h-9 rounded-lg", img: "w-8 h-8" }
        : { wrap: "w-8 h-8 rounded", img: "w-7 h-7" };

  if (src) {
    return (
      <div
        className={cx(
          dims.wrap,
          "bg-white border border-slate-200 flex items-center justify-center overflow-hidden",
          "dark:bg-white/[0.03] dark:border-white/10"
        )}
        aria-hidden="true"
      >
        <img
          src={src}
          alt={title}
          className={cx(dims.img, "object-contain")}
          loading="lazy"
          onError={(e) => {
            // If an image URL is broken, hide it and let fallback glyph be used elsewhere.
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
    );
  }

  if (size === "lg") {
    return (
      <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl bg-indigo-500/15 text-indigo-500">
        <span className="text-2xl font-black">{title.slice(0, 1).toUpperCase()}</span>
      </div>
    );
  }

  if (size === "md") {
    return <AppGlyph title={project?.title} division={project?.division} />;
  }

  return (
    <div className="w-8 h-8">
      <AppGlyph title={project?.title} division={project?.division} />
    </div>
  );
}

export default function ProjectsExplorer({
  projects,
  mode = "live", // 'live' | 'uat'
  defaultView = "detail", // 'grid' | 'detail'
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDivision, setActiveDivision] = useState("All");
  const [viewMode, setViewMode] = useState(defaultView);
  const [selectedId, setSelectedId] = useState(projects?.[0]?.id || "");

  const projectDivisionLabel = (division) => {
    if (Array.isArray(division)) {
      const v = division.find(Boolean);
      return v ? String(v) : "General";
    }
    if (division === undefined || division === null) return "General";
    const s = String(division).trim();
    return s || "General";
  };

  const divisions = useMemo(() => {
    const set = new Set();
    (projects || []).forEach((p) => {
      // Firestore data can be inconsistent (string/array/number/null). Normalize to string labels.
      const raw = p?.division;
      if (Array.isArray(raw)) {
        raw.filter(Boolean).forEach((v) => set.add(String(v)));
        return;
      }
      if (raw !== undefined && raw !== null && String(raw).trim()) {
        set.add(String(raw));
      }
    });
    return ["All", ...Array.from(set).sort((a, b) => String(a).localeCompare(String(b)))];
  }, [projects]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (projects || []).filter((p) => {
      const name = formatProjectTitle(p?.title || "").toLowerCase();
      const matchesSearch = !q || name.includes(q);
      const matchesDivision =
        activeDivision === "All" || projectDivisionLabel(p?.division) === activeDivision;
      return matchesSearch && matchesDivision;
    });
  }, [projects, searchQuery, activeDivision]);

  const leFiltered = useMemo(
    () => filtered.filter((p) => p?.projectType === "le"),
    [filtered]
  );

  const mainFiltered = useMemo(
    () => filtered.filter((p) => p?.projectType !== "le"),
    [filtered]
  );

  const showLeGroupDetail = selectedId === LE_GROUP_ID;

  const activeProject = useMemo(() => {
    if (showLeGroupDetail) return null;
    const found = filtered.find((p) => p.id === selectedId);
    return found || filtered[0] || null;
  }, [filtered, selectedId, showLeGroupDetail]);

  const linkForMode = (p) => (mode === "uat" ? p?.uatLink || "" : p?.link || "");

  const isDownloadProject = (p) => Boolean(p?.isDownload);

  const isSmartContactTool = (title) =>
    Boolean(title && title.toLowerCase().includes("smart contact"));

  const mediaVideos = (p) => {
    const vids = p?.videos;
    if (!Array.isArray(vids)) return [];
    return vids
      .map((v) => {
        if (typeof v === "string") return { url: v, title: null };
        return { url: v?.url, title: v?.title || null };
      })
      .filter((v) => typeof v.url === "string" && v.url.trim().length > 0);
  };


  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Category Bar */}
      <div className="px-6 py-2 border-b flex items-center gap-6 overflow-x-auto scrollbar-hide bg-slate-50 border-slate-100 dark:bg-[#0a0a0c] dark:border-white/5">
        {divisions.map((div) => (
          <button
            key={div}
            onClick={() => setActiveDivision(div)}
            className={cx(
              "text-[10px] font-black uppercase tracking-widest transition-all relative py-1 whitespace-nowrap",
              activeDivision === div
                ? "text-indigo-500"
                : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            )}
          >
            {div === "All" ? "All" : String(div)}
            {activeDivision === div && (
              <motion.div
                layoutId="divisionUnderline"
                className="absolute -bottom-2 left-0 right-0 h-0.5 bg-indigo-500 rounded-full"
              />
            )}
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="px-6 py-3 border-b flex items-center gap-3 bg-white border-slate-200 dark:bg-black dark:border-white/5">
        <div className="flex-1" />

        <div className="relative w-72 sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-3 py-2 rounded-md text-xs transition-all outline-none w-full border bg-slate-50 border-slate-200 focus:border-indigo-500 dark:bg-white/5 dark:border-white/10 dark:focus:border-indigo-500"
          />
        </div>

        <div className="flex p-0.5 rounded-md border bg-slate-100 border-slate-200 dark:bg-white/5 dark:border-white/10">
          <button
            onClick={() => setViewMode("grid")}
            className={cx(
              "p-1.5 rounded-md transition-all",
              viewMode === "grid"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                : "text-slate-500"
            )}
            aria-label="Grid view"
            title="Grid view"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setViewMode("detail")}
            className={cx(
              "p-1.5 rounded-md transition-all",
              viewMode === "detail"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                : "text-slate-500"
            )}
            aria-label="Detail view"
            title="Detail view"
          >
            <GalleryVertical className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          {viewMode === "grid" ? (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 h-full overflow-y-auto"
            >
              {filtered.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  className="group border rounded-2xl overflow-hidden transition-all hover:border-indigo-500/50 text-left bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/5 shadow-sm hover:shadow-md"
                  onClick={() => {
                    setSelectedId(p.id);
                    setViewMode("detail");
                  }}
                >
                  {/* Media */}
                  <div className="p-4">
                    <div className="relative h-40 w-full rounded-xl bg-slate-50 border border-slate-100 dark:bg-white/[0.02] dark:border-white/5 overflow-hidden flex items-center justify-center">
                      <img
                        src={p?.images?.[0] || "/placeholder.png"}
                        alt={formatProjectTitle(p.title)}
                        className="h-full w-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.png";
                        }}
                      />
                      <div
                        className={cx(
                          "absolute right-3 top-3 w-1.5 h-1.5 rounded-full",
                          p.status === "comingSoon"
                            ? "bg-amber-500"
                            : "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
                        )}
                        aria-hidden="true"
                      />
                    </div>

                    <h3 className="mt-4 text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                      {formatProjectTitle(p.title)}
                    </h3>
                    <p className="mt-1 text-[11px] text-slate-500 line-clamp-2 leading-relaxed min-h-[32px]">
                      {p.description || "Project workspace application"}
                    </p>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(p.id);
                        setViewMode("detail");
                      }}
                      className="mt-4 w-full py-2 text-[11px] font-black uppercase tracking-widest bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      View More
                    </button>

                    {/* Actions */}
                    <div
                      className="mt-4 flex justify-center items-center gap-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isValidLink(linkForMode(p)) && (
                        <ActionIcon
                          href={linkForMode(p)}
                          title={isDownloadProject(p) ? "Download Link" : "Open App"}
                          download={isDownloadProject(p)}
                          className="bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 hover:scale-110"
                        >
                          {isDownloadProject(p) ? (
                            <Download className="w-5 h-5" style={{ color: "#10B981" }} />
                          ) : (
                            <ExternalLink className="w-5 h-5" style={{ color: "#10B981" }} />
                          )}
                        </ActionIcon>
                      )}

                        {isValidDialNumber(p.dialNumber) && (
                          <ActionIcon
                            href={`tel:${String(p.dialNumber).trim()}`}
                            title={`Call Now ${String(p.dialNumber).trim()}`}
                            className="bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:hover:bg-teal-900/40 hover:scale-110"
                          >
                            <Phone className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                          </ActionIcon>
                        )}

                      {isValidLink(p.reportBugLink) && (
                        <ActionIcon
                          href={p.reportBugLink}
                          title="Report Bug"
                          className="bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 hover:scale-110"
                        >
                          <Bug className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                        </ActionIcon>
                      )}
                      {isValidLink(p.requestFeaturesLink) && (
                        <ActionIcon
                          href={p.requestFeaturesLink}
                          title="Request Features"
                          className="bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 hover:scale-110"
                        >
                          <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </ActionIcon>
                      )}
                      {isSmartContactTool(p.title) && (
                        <>
                          <ActionIcon
                            href="https://chromewebstore.google.com/detail/smart-contact-extension/mmiggdkmkkkmjdpfkmbkbdechhgihlph"
                            title="Chrome Extension"
                            className="bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:scale-110"
                          >
                            <FontAwesomeIcon
                              icon={faChrome}
                              className="w-5 h-5"
                              style={{ color: "#4285F4" }}
                            />
                          </ActionIcon>
                          <ActionIcon
                            href="https://smartcontact.cynetcorp.com/v1/public/firefox-extension/download"
                            title="Firefox Extension"
                            className="bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 hover:scale-110"
                          >
                            <FontAwesomeIcon
                              icon={faFirefox}
                              className="w-5 h-5"
                              style={{ color: "#FF7139" }}
                            />
                          </ActionIcon>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex h-full"
            >
              {/* Master List */}
              <div className="w-80 flex-shrink-0 border-r overflow-y-auto border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-black">
                {mainFiltered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={cx(
                      "w-full flex items-center gap-3 px-4 py-3 border-b text-left transition-all relative",
                      selectedId === p.id
                        ? "bg-indigo-600/10 text-slate-900 dark:text-white"
                        : "border-slate-100 hover:bg-white dark:border-white/5 dark:hover:bg-white/5"
                    )}
                  >
                    {selectedId === p.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
                    )}
                    <div className="w-8 h-8 flex-shrink-0">
                      <ProjectLogo project={p} size="sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold truncate">{formatProjectTitle(p.title)}</h4>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                        {projectDivisionLabel(p.division)}
                      </span>
                    </div>
                    <ChevronRight
                      className={cx(
                        "w-3 h-3 transition-transform",
                        selectedId === p.id
                          ? "translate-x-0 opacity-100"
                          : "opacity-0 -translate-x-2"
                      )}
                    />
                  </button>
                ))}
                {leFiltered.length > 0 && (
                  <button
                    key={LE_GROUP_ID}
                    onClick={() => setSelectedId(LE_GROUP_ID)}
                    className={cx(
                      "w-full flex items-center gap-3 px-4 py-3 border-b text-left transition-all relative",
                      showLeGroupDetail
                        ? "bg-indigo-600/10 text-slate-900 dark:text-white"
                        : "border-slate-100 hover:bg-white dark:border-white/5 dark:hover:bg-white/5"
                    )}
                  >
                    {showLeGroupDetail && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
                    )}
                    <div className="w-8 h-8 flex-shrink-0">
                      <AppGlyph title="LE Projects" division="General" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold truncate">LE Projects</h4>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                        {leFiltered.length} Projects
                      </span>
                    </div>
                    <ChevronRight
                      className={cx(
                        "w-3 h-3 transition-transform",
                        showLeGroupDetail
                          ? "translate-x-0 opacity-100"
                          : "opacity-0 -translate-x-2"
                      )}
                    />
                  </button>
                )}
              </div>

              {/* Detail Panel */}
              <div className="flex-1 overflow-y-auto p-10 bg-white dark:bg-[#0a0a0c]">
                <AnimatePresence mode="wait">
                  {showLeGroupDetail && (
                    <motion.div
                      key={LE_GROUP_ID}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="w-full"
                    >
                      <div className="flex items-center justify-between mb-8">
                        <h2 className="text-4xl font-black">LE Projects</h2>
                        <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-indigo-500/10 text-indigo-500">
                          {leFiltered.length}
                        </span>
                      </div>
                      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/10">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-300">
                              <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider">Project Name</th>
                              <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider">Open App</th>
                              <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider">Bug Form</th>
                              <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider">Feature Form</th>
                              <th className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider">Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leFiltered.map((p) => (
                              <tr
                                key={p.id}
                                className="border-t border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/[0.03] transition"
                              >
                                <td className="px-4 py-3">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedId(p.id)}
                                    className="text-left inline-flex items-center gap-2 hover:text-indigo-600 dark:hover:text-indigo-400"
                                  >
                                    <ProjectLogo project={p} size="sm" />
                                    <span className="text-sm font-semibold truncate">{formatProjectTitle(p.title)}</span>
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  {isValidLink(linkForMode(p)) ? (
                                    <a
                                      href={linkForMode(p)}
                                      target="_blank"
                                      rel="noreferrer"
                                      download={isDownloadProject(p) || undefined}
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40"
                                      title={isDownloadProject(p) ? "Download Link" : "Open App"}
                                    >
                                      {isDownloadProject(p) ? (
                                        <Download className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      ) : (
                                        <ExternalLink className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      )}
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {isValidLink(p.reportBugLink) ? (
                                    <a
                                      href={p.reportBugLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50"
                                      title="Bug Form"
                                    >
                                      <Bug className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {isValidLink(p.requestFeaturesLink) ? (
                                    <a
                                      href={p.requestFeaturesLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                                      title="Feature Form"
                                    >
                                      <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                    </a>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                  {Array.isArray(p.selectedUsers) ? p.selectedUsers.length : 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                  {activeProject && (
                    <motion.div
                      key={activeProject.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="w-full"
                    >
                      <div className="flex items-start justify-between mb-8 gap-6">
                        <div className="flex items-center gap-6 min-w-0">
                          <ProjectLogo project={activeProject} size="lg" />
                          <div className="min-w-0">
                            <h2 className="text-4xl font-black mb-1 truncate">
                              {formatProjectTitle(activeProject.title)}
                            </h2>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-500 text-[10px] font-black uppercase tracking-widest">
                                {mode === "uat" ? "UAT" : "LIVE"}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <div
                                  className={cx(
                                    "w-1.5 h-1.5 rounded-full",
                                    activeProject.status === "comingSoon"
                                      ? "bg-amber-500"
                                      : "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                                  )}
                                />
                                <span className="text-[10px] font-bold uppercase text-slate-500">
                                  {activeProject.status === "comingSoon" ? "Maintenance" : "Online"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Open App button is shown in the action row below */}
                      </div>

                      {/* Actions (replaces Quick Stats area per your request) */}
                      <div
                        className={cx(
                          "grid gap-3 mb-8",
                          isSmartContactTool(activeProject.title)
                            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5"
                            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                        )}
                      >
                        <ActionButton
                          href={isValidLink(linkForMode(activeProject)) ? linkForMode(activeProject) : undefined}
                          disabled={!isValidLink(linkForMode(activeProject))}
                          icon={isDownloadProject(activeProject) ? Download : ExternalLink}
                          label={isDownloadProject(activeProject) ? "Download Link" : "Open App"}
                          download={isDownloadProject(activeProject)}
                          variant="light"
                          className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:hover:bg-green-900/40 dark:text-green-300 dark:border-green-900/30"
                          iconClassName="text-green-500"
                        />
                        {isValidDialNumber(activeProject.dialNumber) && (
                          <ActionButton
                            href={`tel:${String(activeProject.dialNumber).trim()}`}
                            icon={Phone}
                            label="Call Now"
                            title={`Call ${String(activeProject.dialNumber).trim()}`}
                            variant="light"
                            className="bg-teal-50 hover:bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:hover:bg-teal-900/40 dark:text-teal-300 dark:border-teal-900/30"
                            iconClassName="text-teal-500"
                          />
                        )}
                        <ActionButton
                          href={isValidLink(activeProject.reportBugLink) ? activeProject.reportBugLink : undefined}
                          disabled={!isValidLink(activeProject.reportBugLink)}
                          icon={Bug}
                          label="Bug Form"
                          variant="light"
                          className="bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 dark:text-orange-300 dark:border-orange-900/30"
                          iconClassName="text-orange-500"
                        />
                        <ActionButton
                          href={
                            isValidLink(activeProject.requestFeaturesLink)
                              ? activeProject.requestFeaturesLink
                              : undefined
                          }
                          disabled={!isValidLink(activeProject.requestFeaturesLink)}
                          icon={Sparkles}
                          label="Feature Form"
                          variant="light"
                          className="bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 dark:text-purple-300 dark:border-purple-900/30"
                          iconClassName="text-purple-500"
                        />
                        {isSmartContactTool(activeProject.title) && (
                          <>
                            <a
                              href="https://chromewebstore.google.com/detail/smart-contact-extension/mmiggdkmkkkmjdpfkmbkbdechhgihlph"
                              target="_blank"
                              rel="noreferrer"
                              className={cx(
                                "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-[0.98] select-none",
                                "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200",
                                "dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:text-blue-300 dark:border-blue-900/30"
                              )}
                              aria-label="Chrome Extension"
                              title="Chrome Extension"
                            >
                              <FontAwesomeIcon icon={faChrome} className="w-4 h-4" style={{ color: "#4285F4" }} />
                              Chrome Extension
                            </a>
                            <a
                              href="https://smartcontact.cynetcorp.com/v1/public/firefox-extension/download"
                              target="_blank"
                              rel="noreferrer"
                              className={cx(
                                "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-[0.98] select-none",
                                "bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200",
                                "dark:bg-orange-900/20 dark:hover:bg-orange-900/40 dark:text-orange-300 dark:border-orange-900/30"
                              )}
                              aria-label="Firefox Extension"
                              title="Firefox Extension"
                            >
                              <FontAwesomeIcon icon={faFirefox} className="w-4 h-4" style={{ color: "#FF7139" }} />
                              Firefox Extension
                            </a>
                          </>
                        )}
                      </div>

                      <div className="space-y-6">
                        <div>
                          <h3 className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">
                            Application Overview
                          </h3>
                          <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400">
                            {activeProject.description ||
                              "Centralized application workspace for internal tooling, operations, and delivery."}
                          </p>
                        </div>

                        {mediaVideos(activeProject).length > 0 && (
                          <div className="space-y-4">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
                              Training Videos
                            </h3>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {mediaVideos(activeProject).map((v, i) => (
                                <div key={`${v.url}-${i}`} className="space-y-2">
                                  {v.title && (
                                    <div className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">
                                      {v.title}
                                    </div>
                                  )}
                                  <div className="rounded-2xl overflow-hidden border border-slate-200 bg-black/90 dark:border-white/10">
                                    <video
                                      src={v.url}
                                      controls
                                      className="w-full h-64 object-cover bg-black"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128, 128, 128, 0.2); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(128, 128, 128, 0.4); }
      `}</style>
    </div>
  );
}

